// The form builder's palette: each entry becomes a small set of annotations (a question label plus its fields) when dropped on a page.

import type { FieldAnnot, FieldStyle, PdfUserAnnot } from "@fg/web-core/pdf";

export type PaletteKind = "short" | "paragraph" | "choice" | "checkboxes" | "dropdown" | "date" | "number" | "email" | "table" | "signature" | "comb" | "dateComb" | "password" | "multilist" | "photo" | "stamp" | "band" | "imagefield";

export const PALETTE: Array<{ kind: PaletteKind; label: string; hint: string }> = [
    { kind: "short", label: "Short answer", hint: "A single line of text" },
    { kind: "paragraph", label: "Paragraph", hint: "Several lines of text" },
    { kind: "choice", label: "Multiple choice", hint: "Pick one of a few options" },
    { kind: "checkboxes", label: "Checkboxes", hint: "Tick any that apply" },
    { kind: "dropdown", label: "Dropdown", hint: "Choose from a list" },
    { kind: "date", label: "Date", hint: "A date, yyyy-mm-dd" },
    { kind: "number", label: "Number", hint: "A number, with an optional range" },
    { kind: "email", label: "Email", hint: "An email address" },
    { kind: "table", label: "Table", hint: "A grid of answer cells" },
    { kind: "signature", label: "Signature", hint: "A place to sign" },
    { kind: "comb", label: "Boxed ID", hint: "One box per character (PAN, account number, name in block letters)" },
    { kind: "dateComb", label: "Date boxes", hint: "D D M M Y Y Y Y, one box per digit" },
    { kind: "password", label: "Masked text", hint: "Characters shown as dots" },
    { kind: "multilist", label: "List (many)", hint: "A list box where several can be chosen" },
    { kind: "imagefield", label: "Image field", hint: "Fillers click it to choose a picture, drop one on it or paste one; it is kept in the form" },
    { kind: "photo", label: "Photo box", hint: "A bordered box with a caption, for a photograph" },
    { kind: "stamp", label: "Stamp box", hint: "A bordered box for an official stamp" },
    { kind: "band", label: "Section band", hint: "A coloured bar with a section title" }
];

/** How new fields look, chosen once for the form: the same presets as the web forms. */
export interface FormStyle { preset: "outlined" | "filled" | "underline" | "comb" | "rounded"; accent: string }
export const DEFAULT_FORM_STYLE: FormStyle = { preset: "outlined", accent: "#4a73cc" };

/** `hex` mixed with white; amount 0 is white, 1 is the colour. */
export function lighten(hex: string, amount: number): string {
    const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
    if (!m) return "#ffffff";
    const c = [m[1]!, m[2]!, m[3]!].map((h) => Math.round(255 - (255 - parseInt(h, 16)) * amount).toString(16).padStart(2, "0"));
    return `#${c.join("")}`;
}

/** The PDF field look for a form style: MK /BG (tint), MK /BC (border), border width, underline or rounded corners. */
export function styleFor(fs: FormStyle): FieldStyle {
    switch (fs.preset) {
        case "filled": return { fill: lighten(fs.accent, 0.14), stroke: lighten(fs.accent, 0.7), borderWidth: 1.2 };
        case "underline": return { fill: lighten(fs.accent, 0.05), stroke: fs.accent, borderWidth: 1.4, underline: true };
        case "comb": return { fill: "#ffffff", stroke: fs.accent, borderWidth: 0.9 };
        case "rounded": return { fill: lighten(fs.accent, 0.08), stroke: lighten(fs.accent, 0.8), borderWidth: 1, rounded: true };
        default: return { fill: "#ffffff", stroke: lighten(fs.accent, 0.85), borderWidth: 0.8 };
    }
}

/** Header, footer, Bates number and watermark for a range of pages. Text may hold {page}, {pages}, {date} and {name}. */
export interface PageMarks {
    /** "1-3, 5" style; empty is every page. */
    range: string;
    header: [string, string, string];
    footer: [string, string, string];
    bates: { on: boolean; prefix: string; start: number; digits: number };
    watermark: { text: string; color: string; opacity: number };
    fileName: string;
}

/** Page numbers (1-based) a range like "1-3, 5" names; every page when it is empty or unreadable. */
export function pagesInRange(range: string, total: number): number[] {
    const out = new Set<number>();
    for (const part of range.split(",")) {
        const m = /^\s*(\d+)\s*(?:-\s*(\d+))?\s*$/.exec(part);
        if (!m) continue;
        const a = Math.max(1, Number(m[1])), b = Math.min(total, Number(m[2] ?? m[1]));
        for (let i = a; i <= b; i++) out.add(i);
    }
    return out.size ? [...out].sort((x, y) => x - y) : Array.from({ length: total }, (_, i) => i + 1);
}

export interface FieldInfo {
    id: string;
    ftype: FieldAnnot["ftype"];
    name: string;
    label: string;
    required: boolean;
    readOnly: boolean;
    multiline: boolean;
    maxLen: number;
    options: string[];
    validation: NonNullable<FieldAnnot["validation"]>;
    defaultValue: string;
    /** Radio buttons and check boxes: the value this one stands for. */
    exportValue: string;
    style: FieldStyle;
    comb: boolean;
    password: boolean;
    multiSelect: boolean;
    /** Image fields: how the picture is fitted, framed and limited. */
    image?: import("@fg/web-core/pdf").ImageFieldOpts;
}

export const IMAGE_FIELD_NAME = "photo";

const text = (id: string, page: number, x: number, y: number, w: number, line: string, size = 11, bold = false): PdfUserAnnot => ({
    id, kind: "freetext", page, rect: [x, y - size * 1.5 - 2, x + w, y], lines: [line], fontSize: size, color: "#202124", bold, family: "Helvetica", fill: null
});

/** The question `n` in a form: names stay unique by counting up from the highest existing "q<number>". */
export function nextName(existing: string[]): string {
    let n = 0;
    for (const e of existing) { const m = /^q(\d+)/.exec(e); if (m) n = Math.max(n, Number(m[1])); }
    return `q${n + 1}`;
}

/** The annotations for one palette entry dropped with its top-left corner at `pt` (PDF user space). */
export function buildFieldSet(kind: PaletteKind, page: number, pt: [number, number], existingNames: string[], newId: () => string, style?: FieldStyle): PdfUserAnnot[] {
    const name = nextName(existingNames);
    const [x, top] = pt;
    const base = { page, required: false, readOnly: false, multiline: false, options: [] as string[], maxLen: 0 };
    const out: PdfUserAnnot[] = [];
    const title = (label: string) => { out.push(text(newId(), page, x, top, 260, label, 11, true)); return top - 20; };
    const field = (o: Partial<FieldAnnot> & Pick<FieldAnnot, "ftype" | "rect">, label: string): FieldAnnot => ({ id: newId(), kind: "field", name, label, ...base, ...(style ? { style } : {}), ...o });
    switch (kind) {
        case "short": case "date": case "number": case "email": {
            const label = kind === "short" ? "Short answer" : kind === "date" ? "Date" : kind === "number" ? "Number" : "Email";
            const y = title(label);
            const validation = kind === "date" ? { type: "date" as const } : kind === "number" ? { type: "number" as const } : kind === "email" ? { type: "email" as const } : undefined;
            out.push(field({ ftype: "text", rect: [x, y - 22, x + (kind === "short" ? 240 : 160), y], validation }, label));
            break;
        }
        case "paragraph": {
            const y = title("Paragraph");
            out.push(field({ ftype: "text", multiline: true, rect: [x, y - 70, x + 280, y] }, "Paragraph"));
            break;
        }
        case "dropdown": {
            const y = title("Dropdown");
            out.push(field({ ftype: "combo", options: ["Option 1", "Option 2", "Option 3"], rect: [x, y - 22, x + 180, y] }, "Dropdown"));
            break;
        }
        case "choice": case "checkboxes": {
            let y = title(kind === "choice" ? "Multiple choice" : "Checkboxes");
            for (let i = 1; i <= 3; i++) {
                const ex = `Option ${i}`;
                out.push(field({ ftype: kind === "choice" ? "radio" : "check", name: kind === "choice" ? name : `${name}_${i}`, exportValue: kind === "choice" ? ex : "Yes", rect: [x, y - 14, x + 14, y] }, kind === "choice" ? "Multiple choice" : ex));
                out.push(text(newId(), page, x + 22, y + 1, 200, ex, 11));
                y -= 22;
            }
            break;
        }
        case "table": {
            const cols = 3, rows = 3, cw = 100, rh = 22;
            let y = title("Table");
            for (let c = 0; c < cols; c++) out.push(text(newId(), page, x + c * cw, y, cw - 4, `Column ${c + 1}`, 10, true));
            y -= 18;
            for (let r = 0; r < rows; r++) {
                for (let c = 0; c < cols; c++) out.push(field({ ftype: "text", name: `${name}_r${r + 1}c${c + 1}`, rect: [x + c * cw, y - rh + 2, x + c * cw + cw - 4, y] }, `Row ${r + 1}, column ${c + 1}`));
                y -= rh;
            }
            break;
        }
        case "signature": {
            const y = title("Signature");
            out.push(field({ ftype: "signature", rect: [x, y - 44, x + 200, y] }, "Signature"));
            break;
        }
        case "comb": {
            const y = title("Boxed ID");
            out.push(field({ ftype: "text", comb: true, maxLen: 12, rect: [x, y - 24, x + 12 * 20, y] }, "Boxed ID"));
            break;
        }
        case "dateComb": {
            const y = title("Date (DD MM YYYY)");
            out.push(field({ ftype: "text", comb: true, maxLen: 8, validation: { type: "regex", pattern: "^\\d{8}$", message: "Enter the date as 8 digits, DDMMYYYY." }, rect: [x, y - 24, x + 8 * 20, y] }, "Date (DD MM YYYY)"));
            break;
        }
        case "password": {
            const y = title("Masked text");
            out.push(field({ ftype: "text", password: true, rect: [x, y - 22, x + 200, y] }, "Masked text"));
            break;
        }
        case "multilist": {
            const y = title("Choose any");
            out.push(field({ ftype: "list", multiSelect: true, options: ["Option 1", "Option 2", "Option 3", "Option 4"], rect: [x, y - 64, x + 180, y] }, "Choose any"));
            break;
        }
        case "imagefield": {
            const y = title("Photo");
            const names = new Set(existingNames);
            let n = 1, nm = IMAGE_FIELD_NAME;
            while (names.has(nm)) nm = `${IMAGE_FIELD_NAME}${++n}`;
            out.push(field({ ftype: "image", name: nm, rect: [x, y - 120, x + 100, y], image: { opts: { fit: "fit", shape: "square", placeholder: "", maxBytes: 0, types: [], maxPx: 1600, border: [0.45, 0.5, 0.6] } } }, "Photo"));
            break;
        }
        case "photo": case "stamp": {
            const w = kind === "photo" ? 100 : 120, h = kind === "photo" ? 120 : 90;
            const col = style?.stroke ?? "#4a73cc";
            out.push({ id: newId(), kind: "square", page, rect: [x, top - h, x + w, top], stroke: col, fill: null, width: 1.2 });
            out.push(text(newId(), page, x + 6, top - h / 2 + 8, w - 12, kind === "photo" ? "Affix recent photograph here" : "Stamp", 9));
            break;
        }
        case "band": {
            out.push({ id: newId(), kind: "freetext", page, rect: [x, top - 22, x + 480, top], lines: ["SECTION TITLE"], fontSize: 11, color: "#0f172a", bold: true, family: "Helvetica", fill: style?.fill ?? "#bfdbfe" });
            break;
        }
    }
    return out;
}
